import type { TextAnnotation, TextParagraph } from '../model';
import { formatRange } from '../domain/richText.ts';
import { plainText } from '../rendering/textLayout.ts';

/**
 * ADR 0014 amendment (#1137, owner 2026-10-31) -- text a chemist writes by a reaction arrow, read:
 * conditions with units fill the Conditions card, known words are notes, anything else is a
 * reagent's name or formula (looked up elsewhere). Pure: no lookups here.
 */
export interface ReadReactionText {
  readonly conditions: {
    readonly temperatureC?: number;
    readonly pressureAtm?: number;
    readonly durationS?: number;
    readonly yieldPercent?: number;
  };
  /** Words a scheme writes by the arrow -- reflux, Δ, hν, rt, overnight -- as typed. */
  readonly notes: readonly string[];
  /** Everything else: a reagent's name or formula (normalised: H₂SO₄ -> H2SO4), and whether it was
   *  marked a catalyst ("cat."). */
  readonly reagents: readonly { readonly name: string; readonly catalyst: boolean }[];
}

const SUBSCRIPT_DIGITS: Record<string, string> = {
  '₀': '0',
  '₁': '1',
  '₂': '2',
  '₃': '3',
  '₄': '4',
  '₅': '5',
  '₆': '6',
  '₇': '7',
  '₈': '8',
  '₉': '9'
};

/** A text annotation's words: runs joined, lines as separate pieces; a superscript "o" before C or F
 *  (typed as a degree sign) read as "°". */
export function textOf(annotation: TextAnnotation): string {
  return annotation.paragraphs
    .map((paragraph) =>
      paragraph.runs.map((run) => (run.script === 'super' && /^[oº]$/.test(run.text) ? '°' : run.text)).join('')
    )
    .join('\n');
}

const NUMBER = String.raw`(-?\d+(?:[.,]\d+)?)`;
const TEMPERATURE = new RegExp(String.raw`^${NUMBER}\s*(?:°|º|˚|deg\.?\s*)\s*([CF])$|^${NUMBER}\s*K$`, 'i');
const TIME = new RegExp(String.raw`^${NUMBER}\s*(s|secs?|seconds?|min|mins|minutes?|h|hrs?|hours?|d|days?)$`, 'i');
const PRESSURE = new RegExp(String.raw`^${NUMBER}\s*(atm|bar|psi|kPa|MPa|mmHg|torr)$`, 'i');
const YIELD = new RegExp(String.raw`^(?:yield\s*)?${NUMBER}\s*%(?:\s*yield)?$`, 'i');
/** Words a scheme writes for how, not what: kept as notes. */
const NOTE_WORDS =
  /^(reflux|Δ|delta|heat|heating|rt|r\.t\.|room\s+temp(?:erature)?|hν|hv|light|uv|microwave|mw|overnight|o\/n|ice|sonication|stir(?:red|ring)?|(?:under\s+)?(?:n2|ar|argon|nitrogen)(?:\s+atm(?:osphere)?)?|inert(?:\s+atm(?:osphere)?)?)$/i;
const AMOUNT = /^\d+(?:[.,]\d+)?\s*(?:eq|equiv|equivalents?|mol\s*%|mmol|mol|mg|g|ml|mL|l|L|M|mM)\.?$/;
const CATALYST = /\s*(?:\(\s*cat(?:alyst|\.)?\s*\)|,?\s*cat(?:alyst|\.)?)$/i;

const toNumber = (text: string) => Number(text.replace(',', '.'));

const SECONDS: Record<string, number> = { s: 1, min: 60, h: 3600, d: 86400 };
function seconds(value: number, unit: string): number {
  const key = /^s/i.test(unit) ? 's' : /^m/i.test(unit) ? 'min' : /^h/i.test(unit) ? 'h' : 'd';
  return value * SECONDS[key]!;
}

const ATM: Record<string, number> = {
  atm: 1,
  bar: 1 / 1.01325,
  psi: 1 / 14.6959,
  kpa: 1 / 101.325,
  mpa: 1000 / 101.325,
  mmhg: 1 / 760,
  torr: 1 / 760
};

/** Reads one text by the arrow. Pieces are split at commas, semicolons and line breaks. */
export function readReactionText(text: string): ReadReactionText {
  const normalised = text.replace(/[₀-₉]/g, (digit) => SUBSCRIPT_DIGITS[digit]!);
  const conditions: { temperatureC?: number; pressureAtm?: number; durationS?: number; yieldPercent?: number } = {};
  const notes: string[] = [];
  const reagents: { name: string; catalyst: boolean }[] = [];
  for (const raw of normalised.split(/[,;\n]+(?![^(]*\))/)) {
    const piece = raw.trim().replace(/\s+/g, ' ');
    if (!piece) continue;
    let match: RegExpExecArray | null;
    if ((match = TEMPERATURE.exec(piece))) {
      if (match[3] !== undefined) conditions.temperatureC = toNumber(match[3]) - 273.15;
      else {
        const value = toNumber(match[1]!);
        conditions.temperatureC = match[2]!.toUpperCase() === 'F' ? ((value - 32) * 5) / 9 : value;
      }
    } else if ((match = TIME.exec(piece))) {
      conditions.durationS = seconds(toNumber(match[1]!), match[2]!);
    } else if ((match = PRESSURE.exec(piece))) {
      conditions.pressureAtm = toNumber(match[1]!) * ATM[match[2]!.toLowerCase()]!;
    } else if ((match = YIELD.exec(piece))) {
      conditions.yieldPercent = toNumber(match[1]!);
    } else if (NOTE_WORDS.test(piece) || AMOUNT.test(piece)) {
      notes.push(piece);
    } else {
      const catalyst = CATALYST.test(piece);
      const name = piece.replace(CATALYST, '').trim();
      if (name) reagents.push({ name, catalyst });
    }
  }
  return { conditions, notes, reagents };
}

/** A formula as typed: element symbols (or abbreviations like Et) with counts, brackets allowed. */
const FORMULA = /^(?:[A-Z][a-z]?\d*|\(|\)\d*)+$/;

/** Whether a reagent's name is a formula with counts to subscript (H2SO4, K2CO3, Et3N). */
export function isFormulaWithCounts(name: string): boolean {
  return FORMULA.test(name) && /\d/.test(name);
}

/**
 * #1137 follow-up (owner, 2026-10-31) -- the counts in each formula among `names`, wherever the
 * text writes it, set as subscripts: H2SO4 reads H₂SO₄. The same paragraphs when nothing changes.
 */
export function withFormulaSubscripts(
  paragraphs: readonly TextParagraph[],
  names: Iterable<string>
): readonly TextParagraph[] {
  const text = plainText(paragraphs);
  let next: readonly TextParagraph[] = paragraphs;
  for (const name of new Set(names)) {
    if (!isFormulaWithCounts(name)) continue;
    const escaped = name.replace(/[()]/g, '\\$&');
    for (const match of text.matchAll(new RegExp(`(?<![A-Za-z0-9])${escaped}(?![A-Za-z0-9])`, 'g'))) {
      for (const count of name.matchAll(/(?<=[A-Za-z)])\d+/g)) {
        const start = match.index + count.index;
        next = formatRange(next, start, start + count[0].length, { script: 'sub' });
      }
    }
  }
  return JSON.stringify(next) === JSON.stringify(paragraphs) ? paragraphs : next;
}
